import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { OperationMode, GeminiResponse, ModeConfig } from '../../types';

// Mode configurations with full details
const MODES: ModeConfig[] = [
  {
    id: OperationMode.ENTITY_INJECTION,
    code: 'ENTITY_INJECTION',
    name: 'Entity Injection',
    description: 'Generate SEO-optimized titles, slugs, and meta descriptions for Dhaka market',
    icon: '🚀',
    placeholder: 'Enter your product name and description...\n\nExample: Premium Mango Pickle - Homemade traditional achar made from Rajshahi mangoes, 500g jar',
    category: 'seo'
  },
  {
    id: OperationMode.SOCIAL_CAPTION,
    code: 'SOCIAL_CAPTION',
    name: 'Social Caption',
    description: 'Create engaging FB/WhatsApp captions with BDT pricing for Bangladesh market',
    icon: '📱',
    placeholder: 'Describe your product with price...\n\nExample: Fresh organic honey from Sundarbans, 500g jar, pure mangrove honey. Price: 850 BDT',
    category: 'social'
  },
  {
    id: OperationMode.F_COMMERCE_CONVERSION,
    code: 'FCOMMERCE_REPLY',
    name: 'F-Commerce Reply',
    description: 'Generate professional public replies and inbox scripts for FB shop conversations',
    icon: '💬',
    placeholder: 'Paste customer message or inquiry...\n\nExample: "Eta ki fresh? Delivery charge koto?"',
    category: 'social'
  },
  {
    id: OperationMode.TRUST_GAP_ANALYSIS,
    code: 'TRUST_GAP_AUDIT',
    name: 'Trust Gap Audit',
    description: 'Analyze BSTI, Halal certification, and trust verification gaps',
    icon: '⚖️',
    placeholder: 'Enter your business/product details for trust audit...\n\nExample: Online grocery store selling imported food items, cooking oil, spices',
    category: 'audit'
  },
  {
    id: OperationMode.EXPIRY_HELPER,
    code: 'EXPIRY_HELPER',
    name: 'Expiry Helper',
    description: 'Generate expiry management content and clearance sale copies',
    icon: '📅',
    placeholder: 'Enter product details with expiry info...\n\nExample: Nestlé Cerelac 400g, MRP 650 BDT, expiring in 45 days, 200 units in stock',
    category: 'tools'
  },
  {
    id: OperationMode.COMPETITOR_ANALYSIS,
    code: 'COMPETITOR_ANALYSIS',
    name: 'Competitor Analysis',
    description: 'Competitive intelligence and market positioning analysis',
    icon: '🔍',
    placeholder: 'Enter competitor info or your niche...\n\nExample: Compare my saree business "Tangail Tant House" with Arong, Aarong, and Kay Kraft',
    category: 'research'
  },
  {
    id: OperationMode.JSON_LD_GENERATOR,
    code: 'JSONLD_GENERATOR',
    name: 'JSON-LD Generator',
    description: 'Generate structured data markup for better Google visibility',
    icon: '📊',
    placeholder: 'Enter business/product details...\n\nExample: Restaurant "Kacchi Bhai" in Dhanmondi, Dhaka. Famous for Kacchi Biryani, open 12pm-11pm',
    category: 'seo'
  },
  {
    id: OperationMode.MAPS_GROUNDING,
    code: 'MAPS_GROUNDING',
    name: 'Maps Grounding',
    description: 'Google Maps optimization and local search enhancement',
    icon: '📍',
    placeholder: 'Enter your business location details...\n\nExample: Mobile repair shop in Elephant Road, Dhaka. Need to improve Google Maps visibility',
    category: 'research'
  },
  {
    id: OperationMode.IMAGE_EDITING,
    code: 'AI_ART_PROMPT',
    name: 'AI Art Prompt',
    description: 'Generate product photography prompts and image descriptions',
    icon: '🎨',
    placeholder: 'Describe the image you want to create...\n\nExample: Professional product photo of handmade leather wallet, dark wooden background, soft lighting',
    category: 'creative'
  },
  {
    id: OperationMode.INFOGRAPHIC_CONTENT,
    code: 'INFOGRAPHIC',
    name: 'Infographic',
    description: 'Create visual content ideas and infographic text',
    icon: '📈',
    placeholder: 'Enter topic for infographic...\n\nExample: Benefits of buying local products in Bangladesh - for social media post',
    category: 'creative'
  },
  {
    id: OperationMode.SAAS_FRAMEWORK,
    code: 'SAAS_FRAMEWORK',
    name: 'SaaS Framework',
    description: 'Technical framework and architecture recommendations',
    icon: '🗝️',
    placeholder: 'Describe your SaaS idea...\n\nExample: Inventory management system for small F-commerce businesses in Bangladesh',
    category: 'technical'
  },
  {
    id: OperationMode.MAP_PCP_CLIENTS,
    code: 'MAP_PCP_CLIENTS',
    name: 'Map PCP Clients',
    description: 'Potential client profiling and market mapping',
    icon: '🗺️',
    placeholder: 'Describe your target market...\n\nExample: Looking for potential B2B clients for packaging solutions in Dhaka industrial areas',
    category: 'research'
  },
  {
    id: OperationMode.MARKET_LINKAGE,
    code: 'MARKET_LINKAGE',
    name: 'Market Linkage AI',
    description: 'Supply chain and market connection recommendations',
    icon: '🌉',
    placeholder: 'Describe your market linkage need...\n\nExample: Small fish farmer in Mymensingh looking to connect with Dhaka wholesale markets',
    category: 'research'
  },
  {
    id: OperationMode.VIDEO_GENERATION,
    code: 'VIDEO_GENERATION',
    name: 'Video Generation',
    description: 'Generate video scripts and storyboards for product marketing',
    icon: '🎬',
    placeholder: 'Describe your video concept...\n\nExample: 30-second promotional video for new restaurant opening in Gulshan',
    category: 'creative'
  }
];

const CATEGORIES = [
  { id: 'all', name: 'All Tools', icon: '🎯' },
  { id: 'seo', name: 'SEO', icon: '🔎' },
  { id: 'social', name: 'Social Media', icon: '📱' },
  { id: 'creative', name: 'Creative', icon: '🎨' },
  { id: 'research', name: 'Research', icon: '🔍' },
  { id: 'audit', name: 'Audit', icon: '⚖️' },
  { id: 'tools', name: 'Tools', icon: '🛠️' },
  { id: 'technical', name: 'Technical', icon: '⚙️' }
];

const ClientEngine: React.FC = () => {
  const { hasFeature, availableFeatures } = useAuth();
  const [selectedMode, setSelectedMode] = useState<ModeConfig | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [input, setInput] = useState('');
  const [output, setOutput] = useState<GeminiResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usageInfo, setUsageInfo] = useState<{ today: number; limit: number } | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadUsageInfo();
  }, []);

  const loadUsageInfo = async () => {
    const response = await api.getClientDashboard();
    if (response.success && response.data) {
      setUsageInfo({
        today: response.data.usageToday,
        limit: response.data.dailyLimit
      });
    }
  };

  const filteredModes = selectedCategory === 'all'
    ? MODES
    : MODES.filter(m => m.category === selectedCategory);

  const canAccessMode = (mode: ModeConfig): boolean => {
    if (availableFeatures.includes('ALL')) return true;
    return hasFeature(mode.code);
  };

  const handleProcess = async () => {
    if (!selectedMode || !input.trim()) return;

    if (!canAccessMode(selectedMode)) {
      setError('This feature is not available in your current plan. Please upgrade to access.');
      return;
    }

    setLoading(true);
    setError(null);
    setOutput(null);

    const response = await api.processEngineRequest(selectedMode.id, input);

    if (response.success && response.data) {
      setOutput(response.data);
      // Refresh usage info
      loadUsageInfo();
    } else {
      setError(response.error || 'Failed to process request');
    }

    setLoading(false);
  };

  const handleCopy = async () => {
    if (output?.text) {
      await navigator.clipboard.writeText(output.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClear = () => {
    setInput('');
    setOutput(null);
    setError(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">GEO-PRO Engine</h1>
          <p className="text-neutral-500">AI-powered tools for Bangladesh e-commerce optimization</p>
        </div>
        {usageInfo && (
          <div className="flex items-center gap-4">
            <div className="bg-[#121212] border border-[#262626] rounded-xl px-4 py-2">
              <div className="flex items-center gap-2">
                <span className="text-neutral-500 text-sm">Today's Usage:</span>
                <span className={`font-bold ${usageInfo.today >= usageInfo.limit ? 'text-red-400' : 'text-emerald-400'}`}>
                  {usageInfo.today} / {usageInfo.limit}
                </span>
              </div>
              <div className="w-full bg-[#262626] rounded-full h-1.5 mt-1">
                <div
                  className={`h-1.5 rounded-full transition-all ${usageInfo.today >= usageInfo.limit ? 'bg-red-500' : 'bg-emerald-500'}`}
                  style={{ width: `${Math.min((usageInfo.today / usageInfo.limit) * 100, 100)}%` }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              selectedCategory === cat.id
                ? 'bg-emerald-600 text-white'
                : 'bg-[#1a1a1a] border border-[#262626] text-neutral-400 hover:bg-[#262626] hover:text-white'
            }`}
          >
            <span className="mr-2">{cat.icon}</span>
            {cat.name}
          </button>
        ))}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Mode Selection */}
        <div className="lg:col-span-1 space-y-4">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider">Select Tool</h3>
          <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
            {filteredModes.map(mode => {
              const isAccessible = canAccessMode(mode);
              const isSelected = selectedMode?.id === mode.id;

              return (
                <button
                  key={mode.id}
                  onClick={() => isAccessible && setSelectedMode(mode)}
                  disabled={!isAccessible}
                  className={`w-full text-left p-4 rounded-xl border transition-all ${
                    isSelected
                      ? 'bg-emerald-600/20 border-emerald-500/50'
                      : isAccessible
                        ? 'bg-[#121212] border-[#262626] hover:bg-[#1a1a1a] hover:border-[#363636]'
                        : 'bg-[#121212]/50 border-[#262626]/50 opacity-50 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{mode.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`font-medium truncate ${isSelected ? 'text-emerald-400' : 'text-white'}`}>
                          {mode.name}
                        </p>
                        {!isAccessible && (
                          <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-[10px] font-bold rounded">
                            UPGRADE
                          </span>
                        )}
                      </div>
                      <p className="text-neutral-500 text-xs mt-1 line-clamp-2">{mode.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Input/Output Area */}
        <div className="lg:col-span-2 space-y-4">
          {selectedMode ? (
            <>
              {/* Selected Mode Header */}
              <div className="bg-[#121212] border border-[#262626] rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{selectedMode.icon}</span>
                  <div>
                    <h3 className="text-lg font-bold text-white">{selectedMode.name}</h3>
                    <p className="text-neutral-500 text-sm">{selectedMode.description}</p>
                  </div>
                </div>
              </div>

              {/* Input Area */}
              <div className="bg-[#121212] border border-[#262626] rounded-xl overflow-hidden">
                <div className="p-3 border-b border-[#262626] flex items-center justify-between">
                  <span className="text-xs font-bold text-neutral-500 uppercase">Input</span>
                  <button
                    onClick={handleClear}
                    className="text-xs text-neutral-500 hover:text-white transition-colors"
                  >
                    Clear
                  </button>
                </div>
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={selectedMode.placeholder}
                  className="w-full bg-transparent text-white p-4 min-h-[200px] resize-none focus:outline-none placeholder:text-neutral-600"
                />
                <div className="p-3 border-t border-[#262626] flex items-center justify-between">
                  <span className="text-xs text-neutral-500">
                    {input.length} characters
                  </span>
                  <button
                    onClick={handleProcess}
                    disabled={loading || !input.trim()}
                    className={`px-6 py-2 rounded-xl font-medium transition-all flex items-center gap-2 ${
                      loading || !input.trim()
                        ? 'bg-emerald-600/50 text-white/50 cursor-not-allowed'
                        : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                    }`}
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <span>✨</span>
                        Generate
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-xl">⚠️</span>
                    <div>
                      <p className="font-medium text-red-400">Error</p>
                      <p className="text-red-400/80 text-sm mt-1">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Output Area */}
              {output && (
                <div className="bg-[#121212] border border-[#262626] rounded-xl overflow-hidden">
                  <div className="p-3 border-b border-[#262626] flex items-center justify-between">
                    <span className="text-xs font-bold text-neutral-500 uppercase">Output</span>
                    <div className="flex items-center gap-2">
                      {output.processingTime && (
                        <span className="text-xs text-neutral-500">
                          {output.processingTime}ms
                        </span>
                      )}
                      <button
                        onClick={handleCopy}
                        className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                          copied
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : 'bg-[#1a1a1a] text-neutral-400 hover:bg-[#262626] hover:text-white'
                        }`}
                      >
                        {copied ? '✓ Copied' : 'Copy'}
                      </button>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="prose prose-invert max-w-none">
                      <pre className="whitespace-pre-wrap text-white font-sans text-sm leading-relaxed">
                        {output.text}
                      </pre>
                    </div>
                  </div>

                  {/* Grounding Links */}
                  {output.groundingLinks && output.groundingLinks.length > 0 && (
                    <div className="p-4 border-t border-[#262626]">
                      <p className="text-xs font-bold text-neutral-500 uppercase mb-2">Sources</p>
                      <div className="flex flex-wrap gap-2">
                        {output.groundingLinks.map((link, idx) => (
                          <a
                            key={idx}
                            href={link.uri}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1 bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs rounded-lg hover:bg-blue-500/20 transition-colors"
                          >
                            {link.title || `Source ${idx + 1}`} ↗
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Token Usage */}
                  {output.tokensUsed && (
                    <div className="px-4 py-2 border-t border-[#262626] bg-[#0a0a0a]">
                      <span className="text-xs text-neutral-500">
                        Tokens used: {output.tokensUsed}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            /* No Mode Selected State */
            <div className="bg-[#121212] border border-[#262626] rounded-xl p-12 text-center">
              <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">🎯</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Select a Tool</h3>
              <p className="text-neutral-500 max-w-md mx-auto">
                Choose one of the AI-powered tools from the left panel to start generating optimized content for your Bangladesh business.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Feature Highlight */}
      <div className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border border-emerald-500/20 rounded-2xl p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-white mb-2">🇧🇩 Optimized for Bangladesh Market</h3>
            <p className="text-neutral-400 text-sm">
              All outputs are tailored for the Bangladesh e-commerce ecosystem - with proper BDT pricing format,
              Bangla-English mixed content support, local SEO optimization, and compliance with BSTI/Halal requirements.
            </p>
          </div>
          <div className="flex gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-400">14</p>
              <p className="text-xs text-neutral-500">AI Tools</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-400">24/7</p>
              <p className="text-xs text-neutral-500">Available</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientEngine;
